import React from 'react';
import PropTypes from 'prop-types';
import RoleAlignmentForm, { RoleAlignmentResults } from './RoleAlignmentForm';

// Managerial Assessment — the manager's companion to the Role Alignment
// Questionnaire. A direct manager completes one per report who has submitted
// theirs; the two are read side by side in the Role Alignment Conversation.
//
// This is a schema plus a thin wrapper: RoleAlignmentForm is already a
// schema-driven renderer, so the whole form is the definition below. It never
// appears under To-Do → Forms because it isn't granted via enabled_forms — it
// surfaces on the Team page for the one manager who owes it (see
// routers/forms.py: ASSESSMENT_KEY).

const RUNGS = [
  'Advisor Path: Client Service Associate',
  'Advisor Path: Paraplanner',
  'Advisor Path: Advisor',
  'Advisor Path: Lead Advisor',
  'Advisor Path: Practicing Partner',
  'Operations Path: Client Service Administrator',
  'Operations Path: Senior Client Service Administrator',
  'Operations Path: Manager',
  'Operations Path: Director',
  'Operations Path: Officer',
];

// The firm's competency dimensions, rated on demonstrated performance. N/A is a
// real answer here — a manager who hasn't observed a dimension shouldn't be
// forced to guess, and a guessed 3 is worse than an honest blank.
const COMPETENCIES = [
  'People First', 'Ownership Mindset', 'Hungry, Yet Humble', 'Positive, Grateful, Fun',
  'Innovation to Evolution', 'AI Native', 'Outcome-Accountable', 'Execution Excellence',
  'Resourceful Problem Solver', 'Rapid Adapter', 'Performance Multiplier',
  'Capacity to take on more', 'Effective Communication',
];

// Same four zones as the employee's Activity Snapshot, worded from the manager's
// vantage point. Keeping the keys identical (ua/ex/co/ic) is what lets the two
// submissions be compared zone by zone.
const OBSERVED_ZONES = [
  { key: 'ua', title: 'Unique Ability', color: '#2E7D32', bg: '#E8F5E9',
    placeholder: 'Activities where this person clearly shines...',
    desc: ['Clear superior skill', 'Visibly energized by it', 'Keeps getting better at it'] },
  { key: 'ex', title: 'Excellent', color: '#1565C0', bg: '#E3F2FD',
    placeholder: 'Activities they are good at but may not love...',
    desc: ['Strong skill, relied on for it', 'Does not seem to energize them', 'Reputation built on it'] },
  { key: 'co', title: 'Competent', color: '#F57F17', bg: '#FFF8E1',
    placeholder: 'Activities that are merely adequate...',
    desc: ['Meets the standard', 'Someone else could do it better', 'Little growth in it'] },
  { key: 'ic', title: 'Incompetent', color: '#B71C1C', bg: '#FFEBEE',
    placeholder: 'Activities that are a poor fit...',
    desc: ['Struggles or avoids', 'Source of errors or stress', 'Should be reassigned'] },
];

const LANES = [
  'Financial Services (Advisory, Tax, Medicare)',
  'Advisor Experience (Operations)',
  'Advisor Success (Learning, Events, Integration)',
  'Finance / Accounting', 'Marketing', 'Business Development',
  'Compliance / Supervision', 'Technology', 'People / HR', 'Other / unsure',
];

export const MANAGERIAL_ASSESSMENT_VERSION = 1;

export const MANAGERIAL_ASSESSMENT_SCHEMA = [
  {
    num: 'Section 1', title: 'Employee and Assessor Information',
    blocks: [
      { kind: 'fields', fields: [
        { name: 'employee_name', label: 'Employee Being Assessed', type: 'text', col: 12 },
        { name: 'current_firm', label: 'Current Firm', type: 'text', col: 6 },
        { name: 'current_title', label: 'Current Title', type: 'text', col: 6 },
        { name: 'managed_duration', label: 'Length of Time You Have Managed This Person', type: 'text', col: 6 },
        { name: 'office_location', label: 'Office Location', type: 'text', col: 6 },
        { name: 'assessor_name', label: 'Assessor Name', type: 'text', col: 6 },
        { name: 'assessor_title', label: 'Assessor Title', type: 'text', col: 6 },
        { name: 'date_completed', label: 'Date Completed', type: 'text', col: 6, autoDate: true },
      ] },
    ],
  },
  {
    num: 'Section 2', title: 'Competency and Performance Assessment',
    blocks: [
      { kind: 'note', text: 'Rate this person on each dimension based on demonstrated performance, not potential. 1 = Significant development needed, 3 = Meets expectations, 5 = Exceptional / top of peer group. Mark N/A if you have not observed the dimension.' },
      { kind: 'ratings', name: 'competencies', label: 'Competency ratings',
        rowHeader: 'Dimension', rows: COMPETENCIES, scale: [1, 2, 3, 4, 5, 'N/A'] },
      { kind: 'fields', fields: [
        { name: 'rating_explanations', label: 'Briefly explain any rating of 1 or 5. Specific examples are more useful than general statements.', type: 'textarea', col: 12, rows: 3 },
      ] },
    ],
  },
  {
    num: 'Section 3', title: 'Unique Ability Observation',
    blocks: [
      { kind: 'callout', variant: 'blue', title: 'About This Section', paragraphs: [
        'The employee has completed a Unique Ability exercise sorting their activities into four zones. From your vantage point as their manager, estimate where they actually spend time today and where they do their best work.',
        'Your read often differs from theirs, which is exactly what makes this useful — the gaps are the most valuable part of the conversation.',
      ] },
      { kind: 'note', text: 'For each zone, note the activities you observe and estimate the share of their time spent there today. The four percentages should total 100%.' },
      { kind: 'quadrant', zones: OBSERVED_ZONES },
      { kind: 'subheader', text: 'Keep and Shed' },
      { kind: 'fields', fields: [
        { name: 'should_keep', label: 'Responsibilities they should keep and lean into', type: 'textarea', col: 6, rows: 3 },
        { name: 'should_shed', label: 'Responsibilities they should hand off or stop', type: 'textarea', col: 6, rows: 3 },
      ] },
    ],
  },
  {
    num: 'Section 4', title: 'Career Path and Rung Recommendation',
    blocks: [
      { kind: 'subheader', text: '4A. Recommended Path' },
      { kind: 'fields', fields: [
        { name: 'recommended_path', label: 'Recommended Path', type: 'radio', hideLabel: true,
          options: ['Advisor Career Path', 'Operations Career Path',
                    'Either could work; depends on development and interest'] },
      ] },
      { kind: 'subheader', text: '4B. Rung Placement' },
      { kind: 'fields', fields: [
        { name: 'rung_today', label: 'Rung they are performing at today', type: 'select', options: RUNGS, col: 6 },
        { name: 'rung_ceiling', label: 'Rung you believe they can reach (ceiling)', type: 'select', options: RUNGS, col: 6 },
        { name: 'rung_correct', label: 'Are they currently in the right rung? If over- or under-leveled, explain.', type: 'textarea', col: 12, rows: 3 },
      ] },
      { kind: 'subheader', text: '4C. People Leadership Potential' },
      { kind: 'fields', fields: [
        { name: 'leadership_potential', label: 'People Leadership Potential', type: 'radio', hideLabel: true,
          options: ['Strong leader today; should manage a team',
                    'Has potential; would need development before managing',
                    'Best as a strong individual contributor',
                    'Currently manages but may be better suited elsewhere'] },
      ] },
    ],
  },
  {
    num: 'Section 5', title: 'Lane and Seat Recommendation',
    blocks: [
      { kind: 'note', text: "Based on this person's strengths and Unique Ability, check the lane(s) where you believe they fit best in the Prosperity structure. Select all that apply." },
      { kind: 'fields', fields: [
        { name: 'recommended_lanes', label: 'Recommended lanes', type: 'checkgroup', hideLabel: true, options: LANES },
        { name: 'recommended_seat', label: 'What specific seat or role do you recommend, and why is it the right fit?', type: 'textarea', col: 12, rows: 3 },
      ] },
    ],
  },
  {
    num: 'Section 6', title: 'Retention and Transition Risk',
    blocks: [
      { kind: 'callout', variant: 'orange', title: 'Why This Matters', paragraphs: [
        'Retention of key people is the single largest driver of integration success. Your honest read here directly informs where leadership focuses its attention during the transition.',
      ] },
      { kind: 'subheader', text: '6A. Retention Priority' },
      { kind: 'note', text: 'How critical is it that we retain this person through the integration?' },
      { kind: 'fields', fields: [
        { name: 'retention_priority', label: 'Retention Priority', type: 'radio', hideLabel: true,
          options: ['Critical; loss would materially hurt the business or client relationships',
                    'High; an important contributor we want to keep',
                    'Standard; valued team member',
                    'Watch; performance or fit concerns to evaluate'] },
      ] },
      { kind: 'subheader', text: '6B. Flight Risk' },
      { kind: 'note', text: 'In your judgment, how likely is this person to leave during the transition?' },
      { kind: 'fields', fields: [
        { name: 'flight_risk', label: 'Flight Risk', type: 'radio', hideLabel: true,
          options: ['Low; engaged and likely to stay',
                    'Moderate; could be at risk if the transition is handled poorly',
                    'High; actively unsettled or showing signs of disengagement',
                    'Unknown; need to learn more'] },
        { name: 'retention_factors', label: 'What would make this person stay and thrive? What would put them at risk?', type: 'textarea', col: 12, rows: 3 },
      ] },
    ],
  },
  {
    num: 'Section 7', title: 'Summary and Recommendation',
    blocks: [
      { kind: 'subheader', text: '7A. Greatest Strengths' },
      { kind: 'fields', fields: [
        { name: 'strengths', label: 'Greatest Strengths', type: 'textarea', hideLabel: true, col: 12, rows: 3, placeholder: 'What this person does better than anyone...' },
      ] },
      { kind: 'subheader', text: '7B. Development Areas' },
      { kind: 'fields', fields: [
        { name: 'development_areas', label: 'Development Areas', type: 'textarea', hideLabel: true, col: 12, rows: 3, placeholder: 'Where they need to grow to reach their potential...' },
      ] },
      { kind: 'subheader', text: '7C. Overall Recommendation' },
      { kind: 'fields', fields: [
        { name: 'overall_recommendation', label: 'Overall Recommendation', type: 'textarea', hideLabel: true, col: 12, rows: 4, placeholder: 'Your bottom-line recommendation on placement, development, and how to position this person for success at Prosperity...' },
      ] },
      { kind: 'fields', fields: [
        { name: 'assessor_signature', label: 'Assessor Signature', type: 'text', col: 6 },
        { name: 'signature_date', label: 'Date', type: 'text', col: 6, autoDate: true },
      ] },
    ],
  },
];

// Sections hidden from the form for now: the assessment is being run as a
// competency, career-path and retention review only.
//
// They are filtered out here rather than deleted from the schema above so that
// (a) an assessment already filed with those answers still renders in full on
// the results view and in the CSV export, both of which read the whole schema,
// and (b) putting them back is this one list.
//
// The original section numbers are kept, so the form reads 1, 2, 4, 5, 6. That
// matches the paper form HR circulated and keeps the "4A"/"6B" subheaders below
// consistent; renumbering would mean rewriting those too, and unpicking it later.
const HIDDEN_SECTIONS = ['Section 3', 'Section 7'];

export const MANAGERIAL_ASSESSMENT_FILL_SCHEMA =
  MANAGERIAL_ASSESSMENT_SCHEMA.filter((s) => !HIDDEN_SECTIONS.includes(s.num));

function Purpose({ subjectName }) {
  return (
    <div style={{ background: '#fff', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,.08)',
                  padding: '28px 32px', marginBottom: 20 }}>
      <h1 style={{ fontSize: 24, color: '#002040', margin: '0 0 12px', fontWeight: 700 }}>
        Managerial Assessment{subjectName ? `: ${subjectName}` : ''}
      </h1>
      <div style={{ background: '#E8F0F8', borderLeft: '4px solid #0060A0', padding: '18px 22px',
                    fontSize: 14, color: '#444', borderRadius: '0 6px 6px 0' }}>
        <strong style={{ color: '#002040' }}>Purpose</strong><br />
        This is the manager&apos;s companion to the Role Alignment Questionnaire your direct report
        completed. They reflected on their own experience and aspirations; your role is to give a
        candid, experience-based assessment of where this person performs best, where they are
        headed, and how to position them for success at Prosperity.
        <br /><br />
        Complete this after reading their questionnaire — the two are compared side by side during
        the Role Alignment Conversation. Gaps between self-perception and your assessment are not
        problems to hide; they are the most valuable part of the conversation.
        <br /><br />
        Be direct. This shapes seat placement, development planning, and retention priorities
        during the integration.
      </div>
    </div>
  );
}
Purpose.propTypes = { subjectName: PropTypes.string };

export default function ManagerialAssessmentForm({ subjectName, subjectDirectoryId,
                                                   onSubmit, submitting, initial, mode }) {
  return (
    <RoleAlignmentForm
      mode={mode}
      schema={MANAGERIAL_ASSESSMENT_FILL_SCHEMA}
      version={MANAGERIAL_ASSESSMENT_VERSION}
      purpose={<Purpose subjectName={subjectName} />}
      confirmText={`Submit your Managerial Assessment of ${subjectName || 'this employee'}?`}
      initial={initial}
      submitting={submitting}
      // Keyed per subject AND per author. Per subject so a manager with several
      // reports can have more than one assessment in progress; per author
      // because these are candid notes about a named colleague, and on a shared
      // or kiosk browser a subject-only key would hand one manager's unfinished
      // assessment to whoever opened that report's form next.
      //
      // NOTE: unlike the employee questionnaire, this draft is browser-local
      // only — it has no server copy, so it does not survive a different device
      // or a site-data clear.
      storageKey={subjectDirectoryId
        ? `managerial_assessment_${subjectDirectoryId}:${window.localStorage.getItem('e') || ''}`
        : undefined}
      onSubmit={onSubmit}
    />
  );
}

ManagerialAssessmentForm.propTypes = {
  subjectName: PropTypes.string,
  subjectDirectoryId: PropTypes.number,
  onSubmit: PropTypes.func,
  submitting: PropTypes.bool,
  initial: PropTypes.object,
  mode: PropTypes.oneOf(['fill', 'preview']),
};

export function ManagerialAssessmentResults({ responses }) {
  return <RoleAlignmentResults responses={responses} schema={MANAGERIAL_ASSESSMENT_SCHEMA} />;
}
ManagerialAssessmentResults.propTypes = { responses: PropTypes.object };
